import dayjs from 'dayjs';

import { BlogPostDto } from '@/components/BlogPage/types';
import { DEFAULT_OG_IMAGE } from '@/constants/metadata';
import {
	getAuthorEntityId,
	getAuthorUrl,
	getBlogPostEntityId,
	getBlogPostUrl,
	ORGANIZATION_ENTITY_ID
} from '@/constants/schema-ids';
import { extractPlainTextFromBlocksContent } from '@/lib/blocks-content';
import { getMedia } from '@/lib/utils';
import { StrapiImage } from '@/types/common';

const DEFAULT_ARTICLE_IMAGE = {
	width: 1200,
	height: 675
};

const formatSchemaDate = (date?: string): string | undefined => {
	if (!date) return undefined;

	const parsed = dayjs(date);
	if (!parsed.isValid()) return undefined;

	return parsed.toISOString();
};

const isStrapiImage = (media: BlogPostDto['heroMedia']): media is StrapiImage =>
	Boolean(media && 'url' in media && (!('mime' in media) || !media.mime.startsWith('video')));

const getPostImage = (post: BlogPostDto): { url: string; width: number; height: number } => {
	const candidates = [
		post.preview?.image,
		isStrapiImage(post.heroMedia) ? post.heroMedia : null,
		getMedia(post.seo?.preview)
	].filter((image): image is StrapiImage => Boolean(image?.url));

	const image = candidates[0];

	if (image)
		return {
			url: image.url,
			width: image.width ?? DEFAULT_ARTICLE_IMAGE.width,
			height: image.height ?? DEFAULT_ARTICLE_IMAGE.height
		};

	return {
		url: DEFAULT_OG_IMAGE.url,
		width: DEFAULT_ARTICLE_IMAGE.width,
		height: DEFAULT_ARTICLE_IMAGE.height
	};
};

export const buildBlogPostingJsonLd = (post: BlogPostDto) => {
	if (!post.author || !post.seo) return null;

	const slug = post.seo.url;
	const postUrl = getBlogPostUrl(slug);
	const authorSlug = post.author.seo.url;
	const headline = extractPlainTextFromBlocksContent(post.title) || post.seo.title;
	const description = post.seo.description;
	const image = getPostImage(post);
	const datePublished = formatSchemaDate(post.publishDate ?? post.publishedAt);
	// Meaningful update date (not the system updatedAt) — the same chain as in the sitemap.
	const dateModified = formatSchemaDate(
		post.contentUpdatedAt ?? post.publishDate ?? post.publishedAt
	);

	return {
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		'@id': getBlogPostEntityId(slug),
		headline,
		description,
		image: {
			'@type': 'ImageObject',
			url: image.url,
			width: image.width,
			height: image.height
		},
		...(datePublished ? { datePublished } : {}),
		...(dateModified ? { dateModified } : {}),
		author: {
			'@type': 'Person',
			'@id': getAuthorEntityId(authorSlug),
			name: post.author.name,
			url: getAuthorUrl(authorSlug)
		},
		publisher: {
			'@type': 'Organization',
			'@id': ORGANIZATION_ENTITY_ID
		},
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': postUrl
		}
	};
};
